import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Use relative API paths in Docker (proxy handles routing)
// In local dev, the dev server proxy forwards to the backend
const api = axios.create({
  baseURL: '/api',
  withCredentials: true
});

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/user/profile');
      setUser(response.data || null);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const extractAuthError = (error, fallbackKey) => {
    const data = error?.response?.data;
    return {
      error: data?.error,
      errorKey: data?.error_key,
      errorParams: data?.error_params || {},
      fallbackKey,
    };
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const err = extractAuthError(error, 'signInFailed');
      return { 
        success: false, 
        ...err,
      };
    }
  };

  const register = async (username, email, phone, password, dateOfBirth = null) => {
    try {
      // Get referral link code from localStorage if it exists
      const referralLinkCode = localStorage.getItem('referralLinkCode');

      const requestData = {
        username,
        email,
        phone,
        password
      };

      if (dateOfBirth) {
        requestData.dateOfBirth = dateOfBirth;
      }

      // Include referral link code if available
      if (referralLinkCode) {
        requestData.referralLinkCode = referralLinkCode;
      }
      
      const response = await api.post('/register', requestData);
      
      // Clear the referral link code from localStorage after successful registration
      if (referralLinkCode) {
        localStorage.removeItem('referralLinkCode');
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      const err = extractAuthError(error, 'registrationFailedGeneric');
      return { 
        success: false, 
        ...err,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/logout', {});
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setUser(null);
    }
  };

  const registerCompany = async (companyName, email, password) => {
    try {
      const response = await api.post('/company/register', {
        companyName,
        email,
        password,
      });
      setUser(response.data.user);
      return { success: true, data: response.data };
    } catch (error) {
      const err = extractAuthError(error, 'companyRegistrationFailed');
      return {
        success: false,
        ...err,
      };
    }
  };

  const loginCompany = async (email, password) => {
    try {
      const response = await api.post('/company/login', {
        email,
        password,
      });
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const isNetworkError = !error.response;
      const err = extractAuthError(
        error,
        isNetworkError ? 'cannotReachServer' : 'companySignInFailed',
      );
      return {
        success: false,
        ...err,
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    loginCompany,
    register,
    registerCompany,
    logout,
    checkAuthStatus,
    refreshUser: checkAuthStatus,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export { api };